import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { coreAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { isPlatformUser } from '../../lib/companyEditAccess';
import { getApiErrorMessage } from '../../lib/api/types/common';
import { OrgChartNode, NodeType, ALLOWED_CHILDREN, NODE_TYPE_LABELS } from './types';

/** Everything a row needs to take part in dragging. */
export interface OrgChartDnd {
  activeId: string | null;
  pendingMove: PendingMove | null;
  saving: boolean;
  canDrag: (node: OrgChartNode) => boolean;
  canDrop: (draggedId: string, targetId: string) => boolean;
  handleDragStart: (id: string) => void;
  handleDragEnd: (draggedId: string, targetId: string | null) => void;
  confirmMove: () => Promise<void>;
  cancelMove: () => void;
}

/** A move awaiting confirmation. */
export interface PendingMove {
  node: OrgChartNode;
  targetId: number;
  fromName: string;
  toName: string;
}

interface NodeIndexEntry {
  node: OrgChartNode;
  parent: OrgChartNode | null;
}

/** Only these can be picked up — a group is the root of its own tree. */
const MOVABLE_TYPES: NodeType[] = ['division', 'company'];

/** Flatten the tree into a lookup of node id → { node, parent }. */
function indexTree(nodes: OrgChartNode[], parent: OrgChartNode | null, into: Map<string, NodeIndexEntry>) {
  for (const node of nodes) {
    into.set(node.id, { node, parent });
    indexTree(node.children ?? [], node, into);
  }
}

/** True if `candidate` sits anywhere beneath `node`. */
function isDescendant(node: OrgChartNode, candidateId: string): boolean {
  return (node.children ?? []).some(
    (child) => child.id === candidateId || isDescendant(child, candidateId),
  );
}

/**
 * Drag-and-drop reparenting for the org chart.
 *
 * Holds every accepted drop in `pendingMove` rather than saving it, so the
 * caller can confirm before anything is written. Nothing reaches the API until
 * `confirmMove` runs.
 *
 * @param tree - The current hierarchy
 * @param onMoved - Called after a successful move so the caller can refresh
 * @returns Drag handlers, the pending move, and its confirm/cancel actions
 */
export function useOrgChartDnd(tree: OrgChartNode[], onMoved: () => void) {
  const [pendingMove, setPendingMove] = useState<PendingMove | null>(null);
  const [saving, setSaving] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);

  const index = useMemo(() => {
    const map = new Map<string, NodeIndexEntry>();
    indexTree(tree, null, map);
    return map;
  }, [tree]);

  // Moving a company changes its `group`, which only platform users may do —
  // the backend refuses it for anyone else, so the row is not draggable.
  const { user } = useAuth();
  const canMoveCompanies = isPlatformUser(user);

  const canDrag = useCallback(
    (node: OrgChartNode) =>
      MOVABLE_TYPES.includes(node.type) && (node.type !== 'company' || canMoveCompanies),
    [canMoveCompanies],
  );

  /**
   * Whether `draggedId` may be dropped onto `targetId`. Rejects anything the
   * hierarchy disallows, a drop onto itself or its own subtree, and a drop back
   * onto the parent it already has (which would be a no-op).
   */
  const canDrop = useCallback(
    (draggedId: string, targetId: string): boolean => {
      if (draggedId === targetId) return false;
      const dragged = index.get(draggedId);
      const target = index.get(targetId);
      if (!dragged || !target) return false;
      if (!canDrag(dragged.node)) return false;
      if (!(ALLOWED_CHILDREN[target.node.type] ?? []).includes(dragged.node.type)) return false;
      // Never drop a node into its own subtree — that would orphan the branch.
      if (isDescendant(dragged.node, targetId)) return false;
      if (dragged.parent?.id === targetId) return false;
      return true;
    },
    [index, canDrag],
  );

  const handleDragStart = useCallback((id: string) => setActiveId(id), []);

  const handleDragEnd = useCallback(
    (draggedId: string, targetId: string | null) => {
      setActiveId(null);
      if (!targetId || !canDrop(draggedId, targetId)) return;
      const dragged = index.get(draggedId);
      const target = index.get(targetId);
      if (!dragged || !target) return;
      setPendingMove({
        node: dragged.node,
        targetId: target.node.entity_id,
        fromName: dragged.parent?.name ?? 'Top level',
        toName: target.node.name,
      });
    },
    [canDrop, index],
  );

  const cancelMove = useCallback(() => setPendingMove(null), []);

  const confirmMove = useCallback(async () => {
    if (!pendingMove) return;
    const { node, targetId } = pendingMove;
    setSaving(true);
    try {
      // PATCH, not PUT: only the parent changes, and PUT would clear every
      // field left out of the payload.
      if (node.type === 'company') {
        await coreAPI.patchCompany(node.entity_id, { group: targetId });
      } else {
        // A division is a nested group, so it moves by changing its parent.
        await coreAPI.patchCompanyGroup(node.entity_id, { parent_group: targetId });
      }
      toast.success(`${NODE_TYPE_LABELS[node.type]} moved to ${pendingMove.toName}`);
      setPendingMove(null);
      onMoved();
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Could not move it. Nothing was changed.'));
    } finally {
      setSaving(false);
    }
  }, [pendingMove, onMoved]);

  return {
    activeId,
    pendingMove,
    saving,
    canDrag,
    canDrop,
    handleDragStart,
    handleDragEnd,
    confirmMove,
    cancelMove,
  };
}
